import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { Subject, takeUntil, take, timer, Subscription } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ChatService } from '../../services/chat.service';
import { ModernizationAssessmentService } from '../../services/modernization-assessment.service';
import { ChatMessage, ChatSession, AssessmentProgressDto } from '../../models/chat.models';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { ChatMessageComponent } from '../chat-message/chat-message.component';
import { TypingIndicatorComponent } from '../../shared/typing-indicator/typing-indicator.component';
import { ChatInputComponent } from '../chat-input/chat-input.component';
import { UtcDatePipe } from '../../pipes/utc-date.pipe';

@Component({
  selector: 'app-chat-container',
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatTooltipModule,
    MatIconModule,
    MatListModule,
    MatProgressSpinnerModule,
    MatChipsModule,
    ChatMessageComponent,
    TypingIndicatorComponent,
    ChatInputComponent,
    UtcDatePipe,
  ],
  templateUrl: './chat-container.component.html',
  styleUrls: ['./chat-container.component.scss']
})
export class ChatContainerComponent implements OnInit,  OnDestroy, AfterViewChecked {
  @ViewChild('messagesContainer') private messagesContainer!: ElementRef;

  /** Scoped to a specific assessment page - used as context hint for single-app questions. */
  @Input() trackedBusinessApplicationId: number | null = null;

  messages: ChatMessage[] = [];
  sessions: ChatSession[] = [];
  currentSessionId: string | null = null;
  isTyping = false;
  isChatOpen = false;
  isLoadingHistory = false;
  isViewingHistory = false;
  showSessionList = false;
  error: string | null = null;

  /** Inline progress shown when an assessment job is running. */
  activeProgress: AssessmentProgressDto | null = null;

  private destroy$ = new Subject<void>();
  private shouldScrollToBottom = false;
  private progressPollSub: Subscription | null = null;
  private progressLogCursor = 0;

  constructor(
    private chatService: ChatService
  ) {}

  ngOnInit(): void {
    // Subscribe to typing indicator
    this.chatService.isTyping
      .pipe(takeUntil(this.destroy$))
      .subscribe(typing => {
        this.isTyping = typing;
        if (typing) {
          this.shouldScrollToBottom = true;
        }
      });

    // Subscribe to current session changes
    this.chatService.currentSessionId
      .pipe(takeUntil(this.destroy$))
      .subscribe(sessionId => {
        this.currentSessionId = sessionId;
      });

    // Auto-restore the last active session after page refresh or navigation.
    // Only runs once on init; does not mark isViewingHistory (it is the active session, not archive browsing).
    this.chatService.currentSessionId
      .pipe(take(1))
      .subscribe(sessionId => {
        if (sessionId && this.messages.length === 0) {
          this.restoreSessionHistory(sessionId);
        }
      });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollToBottom) {
      this.scrollToBottom();
      this.shouldScrollToBottom = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stopProgressPolling();
  }

  toggleChat(): void {
    this.isChatOpen = !this.isChatOpen;
    if (this.isChatOpen && this.messages.length === 0) {
      this.loadSessions();
    }
  }

  sendMessage(message: string): void {
    this.error = null;
    this.isViewingHistory = false;

    const userMessage: ChatMessage = {
      role: 'User',
      content: message,
      sources: [],
      timestamp: new Date()
    };
    this.messages.push(userMessage);
    this.shouldScrollToBottom = true;

    const assistantMessage: ChatMessage = {
      role: 'Assistant',
      content: '',
      sources: [],
      timestamp: new Date(),
      isStreaming: true
    };
    let assistantMessageAdded = false;

    this.chatService.sendMessageStream(
      message,
      undefined,
      undefined,
      this.trackedBusinessApplicationId ?? undefined
    ).pipe(takeUntil(this.destroy$)).subscribe({
      next: (chunk) => {
        assistantMessage.content += chunk.content;
        if (!assistantMessageAdded && assistantMessage.content.trim().length > 0) {
          this.messages.push(assistantMessage);
          assistantMessageAdded = true;
        }
        if (chunk.isComplete) {
          assistantMessage.isStreaming = false;
          if (chunk.sources) {
            assistantMessage.sources = chunk.sources;
          }
          // Start polling for assessment progress after Nora replies.
          this.startProgressPolling();
        }
        assistantMessage.timestamp = new Date(chunk.timestamp);
        this.shouldScrollToBottom = true;
      },
      error: (err) => {
        assistantMessage.isStreaming = false;
        if (assistantMessageAdded && !assistantMessage.content.trim()) {
          this.messages.pop();
        }
        this.sendMessageFallback(message);
      }
    });
  }

  /**
   * Non-streaming fallback — used when SSE streaming fails.
   */
  private sendMessageFallback(message: string): void {
    this.chatService.sendMessage(
      message,
      undefined,
      undefined,
      this.trackedBusinessApplicationId ?? undefined
    ).subscribe({
      next: (response) => {
        const assistantMessage: ChatMessage = {
          role: 'Assistant',
          content: response.message,
          sources: response.sources,
          timestamp: new Date(response.timestamp)
        };
        this.messages.push(assistantMessage);
        this.shouldScrollToBottom = true;
        this.startProgressPolling();
      },
      error: (err) => {
        this.error = err.error?.message || 'Failed to send message. Please try again.';
        console.error('Chat error:', err);
      }
    });
  }

  // ── Assessment progress polling ──────────────────────────────────────────────

  private startProgressPolling(): void {
    this.stopProgressPolling();
    const sessionId = this.currentSessionId;
    if (!sessionId) return;

    this.progressLogCursor = 0;
    this.progressPollSub = timer(0, 4000).pipe(
      takeUntil(this.destroy$),
      switchMap(() =>
        this.chatService.getAssessmentProgress(sessionId, this.progressLogCursor)
      )
    ).subscribe({
      next: (progress) => {
        if (!progress) {
          // 204 — no active job
          this.activeProgress = null;
          this.stopProgressPolling();
          return;
        }
        this.progressLogCursor = progress.latestLogId;
        this.activeProgress = progress;
        this.updateInlineJobProgress(progress);
        this.shouldScrollToBottom = true;

        if (!progress.isActive) {
          // Job reached a terminal state — inject a brief status message then stop.
          this.injectProgressCompletionMessage(progress);
          this.activeProgress = null;
          this.stopProgressPolling();
        }
      },
      error: () => {
        // Silently stop polling on error (server may have restarted, etc.)
        this.stopProgressPolling();
      }
    });
  }

  private updateInlineJobProgress(progress: AssessmentProgressDto): void {
    const existingMessage = this.messages.find(message => message.jobProgress?.jobId === progress.jobId);
    const targetMessage = existingMessage
      ?? [...this.messages].reverse().find(message => message.role === 'Assistant' && !message.jobProgress);

    if (targetMessage) {
      targetMessage.jobProgress = { ...progress };
    }
  }

  private stopProgressPolling(): void {
    if (this.progressPollSub) {
      this.progressPollSub.unsubscribe();
      this.progressPollSub = null;
    }
  }

  private injectProgressCompletionMessage(progress: AssessmentProgressDto): void {
    let content: string;
    const actionLinks: { label: string; route: string }[] = [];
    const resultRoute = this.getAssessmentRoute(progress);

    if (progress.jobType === 'Readiness' && progress.status === 'Completed') {
      const application = progress.applicationName || `application ${progress.trackedBusinessApplicationId}`;
      const resultLines = [
        progress.readinessScore != null ? `- Readiness score: **${progress.readinessScore}**` : null,
        progress.readinessOutcome ? `- Outcome: **${progress.readinessOutcome}**` : null,
        progress.readinessKeyReason ? `- Key reason: ${progress.readinessKeyReason}` : null,
        progress.readinessRecommendation ? `- Recommendation: ${progress.readinessRecommendation}` : null
      ].filter((line): line is string => line !== null);
      content = `CMDB readiness scoring for **${application}** has completed.`
        + (resultLines.length > 0 ? `\n\n${resultLines.join('\n')}` : '');
      actionLinks.push({ label: 'View Readiness Results', route: resultRoute });
    } else if (progress.status === 'Completed') {
      const scoreText = progress.totalScore != null
        ? `\n\nOverall code assessment score: **${progress.totalScore} / 100**`
        : '';
      content = `Assessment job #${progress.jobId} has **completed**.${scoreText}`;
      actionLinks.push({ label: 'View Assessment Results', route: resultRoute });
    } else {
      const jobLabel = progress.jobType === 'Readiness' ? 'CMDB readiness refresh' : `Assessment job #${progress.jobId}`;
      content = `${jobLabel} **${progress.status.toLowerCase()}**`
        + (progress.errorMessage ? `: ${progress.errorMessage}` : '.');
      actionLinks.push({ label: 'View Details', route: resultRoute });
    }

    const notification: ChatMessage = {
      role: 'Assistant',
      content,
      sources: [],
      timestamp: new Date(),
      actionLinks
    };
    this.messages.push(notification);
    this.shouldScrollToBottom = true;
  }

  private getAssessmentRoute(progress: AssessmentProgressDto): string {
    if (progress.trackedBusinessApplicationId != null) {
      return `/assessment/${progress.trackedBusinessApplicationId}`;
    }

    return `/admin/ai-assessment/job/${progress.jobId}`;
  }

  loadSessions(): void {
    this.chatService.getSessions().subscribe({
      next: (sessions) => {
        this.sessions = sessions;
      },
      error: (err) => {
        console.error('Error loading sessions:', err);
      }
    });
  }

  loadSessionHistory(session: ChatSession): void {
    this.isLoadingHistory = true;
    this.isViewingHistory = true;
    this.showSessionList = false;
    this.chatService.setCurrentSession(session.id);

    this.chatService.getSessionHistory(session.id).subscribe({
      next: (messages) => {
        this.messages = this.normalizeHistoryMessages(messages);
        this.isLoadingHistory = false;
        this.shouldScrollToBottom = true;
        this.startProgressPolling();
      },
      error: (err) => {
        console.error('Error loading session history:', err);
        this.isLoadingHistory = false;
        this.error = 'Failed to load conversation history.';
      }
    });
  }

  startNewConversation(): void {
    this.chatService.startNewConversation();
    this.messages = [];
    this.isViewingHistory = false;
    this.showSessionList = false;
    this.error = null;
  }

  /**
   * Silently restores the message history for the active session on component init.
   * Called automatically when a persisted sessionId is found in localStorage.
   * Unlike loadSessionHistory, this does NOT set isViewingHistory because the
   * user is continuing an active conversation, not browsing the session archive.
   */
  private restoreSessionHistory(sessionId: string): void {
    this.isLoadingHistory = true;

    this.chatService.getSessionHistory(sessionId).subscribe({
      next: (messages) => {
        this.messages = this.normalizeHistoryMessages(messages);
        this.isLoadingHistory = false;
        this.shouldScrollToBottom = true;
        this.startProgressPolling();
      },
      error: () => {
        // Session no longer exists on the server (deleted or expired).
        // Clear the stale ID so the next message starts a fresh session.
        this.chatService.setCurrentSession(null);
        this.isLoadingHistory = false;
      }
    });
  }

  toggleSessionList(): void {
    this.showSessionList = !this.showSessionList;
    if (this.showSessionList) {
      this.loadSessions();
    }
  }

  private normalizeHistoryMessages(messages: ChatMessage[]): ChatMessage[] {
    return messages
      .filter(message => message.content?.trim().length > 0)
      .map(message => ({
        ...message,
        timestamp: new Date(message.timestamp)
      }));
  }

  private scrollToBottom(): void {
    try {
      if (this.messagesContainer) {
        const element = this.messagesContainer.nativeElement;
        element.scrollTop = element.scrollHeight;
      }
    } catch (err) {
      console.error('Error scrolling to bottom:', err);
    }
  }
}
